import {
  isOpenInFocusedSunoTab,
  publishOpenConversation
} from "features/browser-notifications/browser-notifications.util";

type AlertChannel = { myPushTriggerOption?: string };
type AlertMessage = {
  sender?: { userId?: string };
  mentionedUsers?: { userId: string }[];
  mentionType?: string;
};

export const isMentioned = (message: AlertMessage, userId: string) =>
  message.mentionType === "channel" ||
  Boolean(message.mentionedUsers?.some((user) => user.userId === userId));

export function shouldAlertForChatMessage(
  channel: AlertChannel,
  message: AlertMessage,
  userId: string,
  defaultPushOption = "all"
): boolean {
  const pushOption =
    !channel.myPushTriggerOption || channel.myPushTriggerOption === "default"
      ? defaultPushOption
      : channel.myPushTriggerOption;
  if (message.sender?.userId === userId || pushOption === "off") return false;
  return pushOption !== "mention_only" || isMentioned(message, userId);
}

// The conversation on screen in Chat, so a message arriving in it does not pop
// up a notification about what the person is already reading. Chat v2 reports
// it; null whenever no conversation is open. It is shared with the other Suno
// tabs, because whichever tab delivers the alert has to know.
let openChatChannelUrl: string | null = null;

export const setOpenChatChannel = (channelUrl: string | null) => {
  openChatChannelUrl = channelUrl || null;
  publishOpenConversation(openChatChannelUrl);
};

export const getOpenChatChannel = () => openChatChannelUrl;

// `User.chat_notification_seconds`: 0 keeps the notification until it is
// closed; anything else is how many seconds it stays on screen.
export const DEFAULT_CHAT_NOTIFICATION_SECONDS = 15;

export const chatNotificationAutoCloseMs = (
  seconds: number | null | undefined
): number | undefined => {
  const value = seconds ?? DEFAULT_CHAT_NOTIFICATION_SECONDS;
  return value > 0 ? value * 1000 : undefined;
};

// Whether a chat message that passed the mute rules pops up a notification:
// the account setting is on (a backend without it reads as on), and the person
// is not already reading that very conversation in the Suno tab in front (this
// tab, or another one).
export const shouldShowChatNotification = ({
  enabled,
  channelUrl,
  appInFront
}: {
  enabled: boolean | undefined;
  channelUrl: string;
  appInFront: boolean;
}) =>
  enabled !== false &&
  !(appInFront && openChatChannelUrl === channelUrl) &&
  !isOpenInFocusedSunoTab(channelUrl);
